import React from 'react';
import { motion } from 'framer-motion';
import { T, SectionHeading } from './theme';

const fadeUp = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } };
const stagger = { show: { transition: { staggerChildren: 0.08 } } };
const cardHover = { whileHover: { y: -6, scale: 1.03 }, transition: { type: 'spring', stiffness: 300, damping: 20 } };

const VaikhanasamPage = () => (
  <div style={{ background: T.bg, color: T.ink }} className="min-h-screen">

    {/* Hero with Image */}
    <section style={{ background: `linear-gradient(135deg, ${T.navyDark}, ${T.navy})`, position: 'relative', overflow: 'hidden' }}>
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at 60% 40%, rgba(212,175,55,0.1) 0%, transparent 60%)' }} />
      <div className="w-full" style={{ maxHeight: '520px', overflow: 'hidden' }}>
        <img src="/images/Vikhanas.png" alt="Sage Vikhanas"
          style={{ filter: 'grayscale(100%) brightness(0.45)', width: '100%', height: '520px', objectFit: 'cover', objectPosition: 'center 30%' }} />
      </div>
      <div className="absolute inset-0 flex flex-col items-center justify-start pt-12 px-6 text-center">
        <p className="vrv-tag mb-3">Ancient Vedic Heritage</p>
        <h1 className="text-5xl md:text-7xl font-bold text-white mb-4 leading-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>Vaikhanasam</h1>
        <div className="vrv-divider mb-4 mx-auto" style={{ width: '80px' }} />
        <p className="text-base italic max-w-xl" style={{ color: 'rgba(255,255,255,0.55)', fontFamily: '"Cormorant Garamond", serif', fontSize: '1.1rem' }}>
          The Ancient Vedic Tradition of Devotion to Lord Vishnu
        </p>
      </div>
    </section>

    {/* What is Vaikhanasam */}
    <section className="py-20" style={{ background: 'white', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
      <div className="vrv-container">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp}><SectionHeading tag="The Tradition" title="What is Vaikhanasam?" /></motion.div>
          <motion.div variants={fadeUp} className="vrv-card p-8 mb-6" style={{ pointerEvents: 'none' }}>
            <div className="pl-6" style={{ borderLeft: '3px solid #D4AF37' }}>
              <p className="leading-relaxed mb-4 text-base" style={{ color: T.muted, fontFamily: 'Inter, sans-serif' }}>
                Vaikhanasam is one of the most ancient and sacred traditions in Vaishnavism, tracing its origins directly to <strong style={{ color: T.ink }}>Sage Vikhanas</strong>, who was created by Lord Brahma himself. It is considered a direct revelation from the Supreme Lord Vishnu.
              </p>
              <p className="leading-relaxed text-base" style={{ color: T.muted, fontFamily: 'Inter, sans-serif' }}>
                Vaikhanasas are born into the tradition through <strong style={{ color: T.ink }}>Vishnu Bali</strong>, performed during the prenatal stage — a concept known as <em style={{ color: T.navy }}>janma diksha</em>.
              </p>
            </div>
          </motion.div>
          <motion.div variants={fadeUp} className="flex flex-wrap gap-3">
            {['Vedic Origin', 'Unbroken Lineage', 'Temple Tradition', 'Birth Initiation'].map((tag, i) => (
              <span key={i} className="badge badge-navy" style={{ fontSize: '0.7rem', padding: '0.4rem 0.9rem' }}>{tag}</span>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>

    {/* Sage Vikhanas */}
    <section className="py-20" style={{ background: T.bg }}>
      <div className="vrv-container">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}
          className="flex flex-col md:flex-row gap-12 items-center">
          <motion.div variants={fadeUp} className="md:w-2/5">
            <div className="vrv-card p-4" style={{ pointerEvents: 'none' }}>
              <img src="/images/VikhanasaCroppedPNG.png" alt="Sage Vikhanas" className="w-full rounded-2xl" />
              <p className="text-center text-xs mt-3 italic" style={{ color: T.muted, fontFamily: '"Cormorant Garamond", serif' }}>
                Sage Vikhanas — Founder of Vaikhanasa Tradition
              </p>
            </div>
          </motion.div>
          <motion.div variants={fadeUp} className="md:w-3/5">
            <p className="vrv-tag mb-3">The Founder</p>
            <h2 className="vrv-section-title mb-4">Sage Vikhanas</h2>
            <div className="vrv-divider mb-6" style={{ width: '60px' }} />
            <p className="leading-relaxed mb-4 text-sm" style={{ color: T.muted, fontFamily: 'Inter, sans-serif' }}>Sage Vikhanas is the divine progenitor of the Vaikhanasa lineage. Lord Brahma created Vikhanas from his mind (Manasa Putra). Lord Vishnu himself instructed Vikhanas in the methods of worship, temple rituals, and the path of devotion.</p>
            <p className="leading-relaxed mb-4 text-sm" style={{ color: T.muted, fontFamily: 'Inter, sans-serif' }}>Vikhanas had four primary disciples — <strong style={{ color: T.ink }}>Atri, Bhrigu, Kashyapa, and Marichi</strong> — who authored the foundational texts of Vaikhanasa Agama.</p>
            <p className="leading-relaxed text-sm" style={{ color: T.muted, fontFamily: 'Inter, sans-serif' }}>The Vaikhanasa Agama is considered <em style={{ color: T.navy, fontWeight: 600 }}>Shruti</em> (divinely heard), placing it on par with the Vedas.</p>
          </motion.div>
        </motion.div>
      </div>
    </section>

    {/* Legacy of Maharshi Vikhanasa */}
    <section style={{ background: `linear-gradient(160deg, #fdf9ee 0%, #f5f0e0 100%)`, padding: '80px 0', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', right: '-4rem', top: '50%', transform: 'translateY(-50%)', fontFamily: '"Cormorant Garamond", serif', fontSize: '28rem', color: 'rgba(212,175,55,0.05)', lineHeight: 1, userSelect: 'none', pointerEvents: 'none' }}>ॐ</div>
      <div className="vrv-container" style={{ position: 'relative', zIndex: 1 }}>
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
          <div className="flex flex-col lg:flex-row gap-12 items-center">

            {/* Left panel */}
            <motion.div variants={fadeUp} className="lg:w-5/12 w-full flex-shrink-0">
              <div style={{ borderRadius: '28px', background: 'linear-gradient(145deg, #0F172A 0%, #1E293B 100%)',
                border: '1px solid rgba(212,175,55,0.25)', padding: '3rem 2.5rem', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 40% 60%, rgba(212,175,55,0.1) 0%, transparent 65%)', pointerEvents: 'none' }} />
                <div style={{ textAlign: 'center', position: 'relative' }}>
                  <div style={{ fontFamily: '"Cormorant Garamond", serif', fontSize: '4.5rem', color: 'rgba(212,175,55,0.25)', lineHeight: 1, userSelect: 'none', marginBottom: '1.5rem' }}>ॐ</div>
                  <p style={{ fontFamily: 'Poppins, sans-serif', fontSize: '1.1rem', fontWeight: 700, color: 'rgba(230,211,154,0.9)', marginBottom: '1rem' }}>Maharshi Vikhanasa</p>
                  <div style={{ width: '48px', height: '2px', background: '#D4AF37', margin: '0 auto 1.5rem', borderRadius: '2px' }} />
                  <p style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.85rem', color: 'rgba(255,255,255,0.5)', lineHeight: 1.8, fontStyle: 'italic' }}>
                    "Originator of the Vaikhanasa tradition, establishing the sacred path of Vishnu worship through Agamic wisdom and spiritual discipline."
                  </p>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '1.5rem', marginTop: '2rem' }}>
                    {[
                      <svg key="a" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(212,175,55,0.6)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M8 21h12a2 2 0 0 0 2-2v-2H10v2a2 2 0 1 1-4 0V5a2 2 0 1 0-4 0v3h4"/><path d="M19 3H4.5"/><path d="M15 13H9"/><path d="M15 17H9"/><path d="M10 9H9"/></svg>,
                      <svg key="b" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(212,175,55,0.6)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>,
                      <svg key="c" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="rgba(212,175,55,0.6)" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M3 22h18M3 10h18M12 2l9 8H3l9-8zM5 10v12M9 10v12M15 10v12M19 10v12"/></svg>,
                    ]}
                  </div>
                </div>
                <div style={{ height: '2px', background: 'linear-gradient(90deg, transparent, #D4AF37, transparent)', marginTop: '2rem' }} />
              </div>
            </motion.div>

            {/* Right content */}
            <motion.div variants={fadeUp} className="lg:w-7/12 w-full">
              <p className="vrv-tag mb-3">Our Legacy</p>
              <h2 className="vrv-section-title mb-4">Legacy of Maharshi Vikhanasa</h2>
              <div className="vrv-divider mb-6" style={{ width: '60px' }} />
              <p className="leading-relaxed mb-4 text-sm" style={{ color: T.muted, fontFamily: 'Inter, sans-serif' }}>
                Maharshi Vikhanasa is revered as the originator of the Vaikhanasa tradition, establishing a sacred path of Vishnu worship, temple rituals, Agamic wisdom, and spiritual discipline that continues to guide generations across the world.
              </p>
              <p className="leading-relaxed mb-6 text-sm" style={{ color: T.muted, fontFamily: 'Inter, sans-serif' }}>
                The Vaikhanasa Agamas, attributed to his divine inspiration, form the scriptural foundation for temple consecration, daily worship rituals, and the conduct of priests — a living tradition spanning millennia.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label: 'Vaikhanasa Agama scriptures' },
                  { label: 'Vedic rituals & Archana Vidhi' },
                  { label: 'Temple consecration traditions' },
                  { label: 'Global spiritual lineage' },
                ].map(({ label }, i) => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '0.65rem',
                    background: 'rgba(212,175,55,0.06)', borderRadius: '12px', padding: '0.75rem 1rem',
                    border: '1px solid rgba(212,175,55,0.15)' }}>
                    <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#D4AF37', flexShrink: 0 }} />
                    <span style={{ fontFamily: 'Inter, sans-serif', fontSize: '0.82rem', color: T.navy, fontWeight: 500 }}>{label}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>

    {/* Core Principles */}
    <section className="py-20" style={{ background: 'white', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
      <div className="vrv-container">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp}><SectionHeading tag="Foundations" title="Core Principles" /></motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: '🙏', title: 'Vishnu Aradhana', desc: 'Exclusive devotion and worship of Lord Vishnu through prescribed Agama rituals.' },
              { icon: '🔥', title: 'Nitya Karma', desc: 'Daily performance of Vedic rituals including Agnihotra, Sandhyavandana, and Pancha Maha Yagnas.' },
              { icon: '🛕', title: 'Temple Worship', desc: 'Vaikhanasas are hereditary priests of major Vishnu temples, following strict Agama procedures.' },
              { icon: '📜', title: 'Agama Shastra', desc: 'Sacred texts covering temple construction, idol installation, daily worship, and festivals.' },
              { icon: '✨', title: 'Vishnu Bali', desc: 'Prenatal consecration ceremony that initiates the child into Vishnu devotion before birth.' },
              { icon: '🎓', title: 'Vedic Learning', desc: 'Mastering the four Vedas, Agamas, and performing rituals with precision and devotion.' },
            ].map((item, i) => (
              <motion.div key={i} variants={fadeUp} {...cardHover} className="vrv-card p-6" style={{ cursor: 'default' }}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center text-xl mb-4" style={{ background: 'rgba(212,175,55,0.1)' }}>{item.icon}</div>
                <h3 className="text-sm font-bold mb-2 uppercase tracking-wide" style={{ fontFamily: 'Poppins, sans-serif', color: T.navy }}>{item.title}</h3>
                <p className="text-sm leading-relaxed" style={{ color: T.muted, fontFamily: 'Inter, sans-serif' }}>{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>

    {/* Sacred Texts */}
    <section className="py-20" style={{ background: T.bg }}>
      <div className="vrv-container">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp}><SectionHeading tag="Scripture" title="Sacred Texts" /></motion.div>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { title: 'Vikhanas Smarta Sutra', author: 'Sage Vikhanas', desc: 'Foundation text covering domestic rituals and daily observances.' },
              { title: 'Atri Samhita', author: 'Sage Atri', desc: 'Detailed procedures for temple worship and idol consecration.' },
              { title: 'Bhrigu Samhita', author: 'Sage Bhrigu', desc: 'Covers temple architecture, iconography, and festival rituals.' },
              { title: 'Kashyapa Samhita', author: 'Sage Kashyapa', desc: 'Elaborates on daily worship, offerings, and purification rites.' },
              { title: 'Marichi Samhita', author: 'Sage Marichi', desc: 'Focuses on meditation, mantras, and spiritual practices.' },
              { title: 'Khila Tantra', author: 'Various Sages', desc: 'Supplementary texts covering additional rituals and ceremonies.' },
            ].map((text, i) => (
              <motion.div key={i} variants={fadeUp} {...cardHover} className="vrv-card p-6" style={{ cursor: 'default' }}>
                <p className="vrv-tag mb-1">By {text.author}</p>
                <h3 className="text-base font-bold mb-2" style={{ fontFamily: 'Poppins, sans-serif', color: T.navy }}>{text.title}</h3>
                <p className="text-sm" style={{ color: T.muted, fontFamily: 'Inter, sans-serif' }}>{text.desc}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>

    {/* Temples */}
    <section className="py-20" style={{ background: 'white', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
      <div className="vrv-container">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp}><SectionHeading tag="Sacred Spaces" title="Vaikhanasas & Temples" /></motion.div>
          <motion.div variants={fadeUp} className="vrv-card overflow-hidden mb-6" style={{ pointerEvents: 'none' }}>
            <div className="px-8 py-5" style={{ background: '#FDFBF7', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
              <p className="text-sm" style={{ color: T.muted, borderLeft: '3px solid #D4AF37', paddingLeft: '1rem', fontFamily: 'Inter, sans-serif' }}>
                Vaikhanasa priests serve as the hereditary archakas in some of the most revered Vishnu temples in India, performing rituals exactly as prescribed thousands of years ago.
              </p>
            </div>
            <div className="grid sm:grid-cols-2">
              {[
                'Sri Venkateswara Temple, Tirumala',
                'Sri Ranganathaswamy Temple, Srirangam',
                'Sri Varadaraja Perumal Temple, Kanchipuram',
                'Sri Lakshmi Narasimha Temple, Ahobilam',
                'Sri Govindaraja Temple, Tirupati',
                'Sri Kalyana Venkateswara Temple, Srinivasa Mangapuram',
              ].map((temple, i, arr) => (
                <div key={i} className="flex items-center gap-4 px-6 py-4 transition-colors"
                  style={{ borderBottom: i < arr.length - 2 ? '1px solid rgba(0,0,0,0.05)' : 'none', borderRight: i % 2 === 0 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#FDFBF7'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                  <span className="text-lg flex-shrink-0">🛕</span>
                  <p className="text-sm font-medium" style={{ color: T.ink, fontFamily: 'Inter, sans-serif' }}>{temple}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>

    {/* What Makes It Unique */}
    <section className="py-20" style={{ background: T.bg }}>
      <div className="vrv-container">
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={stagger}>
          <motion.div variants={fadeUp}><SectionHeading tag="Distinctiveness" title="What Makes Vaikhanasam Unique" /></motion.div>
          <div className="space-y-4">
            {[
              { title: 'Birth-based Initiation', desc: 'No formal diksha required — Vishnu Bali during pregnancy consecrates the child as a Vaishnava from the womb.', icon: '👶' },
              { title: 'Vedic Authority', desc: 'Vaikhanasa Agama is classified as Shruti, giving it the same authority as the Vedas themselves.', icon: '📖' },
              { title: 'Unbroken Lineage', desc: 'The tradition has been passed down in an unbroken chain from Sage Vikhanas through thousands of years.', icon: '🔗' },
              { title: 'Temple-centric Worship', desc: 'Emphasis on Archa (idol worship) as the highest form of connecting with Lord Vishnu.', icon: '🛕' },
              { title: 'Dual Path', desc: 'Vaikhanasas follow both Grihasta and Vanaprastha paths as prescribed by Vikhanas.', icon: '🌿' },
            ].map((item, i) => (
              <motion.div key={i} variants={fadeUp}
                whileHover={{ y: -4, scale: 1.015 }} transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                className="vrv-card flex items-start gap-5 p-6" style={{ cursor: 'default' }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0" style={{ background: 'rgba(212,175,55,0.1)' }}>{item.icon}</div>
                <div>
                  <h3 className="font-bold text-sm mb-1 uppercase tracking-wide" style={{ fontFamily: 'Poppins, sans-serif', color: T.navy }}>{item.title}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: T.muted, fontFamily: 'Inter, sans-serif' }}>{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>

    {/* Quote Banner */}
    <section className="py-20" style={{ background: `linear-gradient(135deg, ${T.navyDark}, ${T.navy})` }}>
      <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}
        className="vrv-container text-center">
        <blockquote className="text-xl md:text-3xl font-semibold leading-relaxed text-white max-w-3xl mx-auto" style={{ fontFamily: '"Cormorant Garamond", serif', fontStyle: 'italic' }}>
          "Vaikhanasam is not just a tradition — it is the eternal path of devotion,<br />
          <span style={{ color: 'rgba(212,175,55,0.8)' }}>revealed by Lord Vishnu himself to Sage Vikhanas for the welfare of all beings."</span>
        </blockquote>
        <p className="mt-5 text-xs font-bold tracking-widest uppercase" style={{ color: 'rgba(212,175,55,0.4)', fontFamily: 'Poppins, sans-serif' }}>— Vaikhanasa Agama</p>
        <div className="vrv-divider mt-6 mx-auto" style={{ width: '80px' }} />
      </motion.div>
    </section>
  </div>
);

export default VaikhanasamPage;
